import type { App } from 'vue'
import type { Router } from 'vue-router'

/**
 * Error tracking (frontend phase-13 §6).
 *
 * One adapter the whole app reports through. Without `VITE_SENTRY_DSN` it is a console
 * reporter (development, tests, the mocked E2E suite); with one, `@sentry/vue` is loaded
 * **lazily** -- it is ~70KB and belongs on no first load -- and configured with:
 *
 * - **release tagging** from `VITE_APP_RELEASE` (the CI sets it to the commit); source maps
 *   are emitted by the build (`vite.config.ts` `sourcemap: true`) and uploaded by the release
 *   pipeline against the same release name;
 * - **PII scrubbing** in `beforeSend` and `beforeBreadcrumb`: emails, phone numbers, bearer
 *   tokens, six-digit codes, account numbers and long opaque blobs are replaced before anything
 *   leaves the browser, and `sendDefaultPii` is off so no IP or cookie travels either;
 * - **a sampled performance trace** (`VITE_SENTRY_TRACES_SAMPLE_RATE`, default one in ten).
 */
export interface ErrorTracker {
  capture(error: unknown, context?: Record<string, unknown>): void
  captureMessage(message: string, context?: Record<string, unknown>): void
}

export const REDACTED = '[redacted]'

const PATTERNS: RegExp[] = [
  /\bbearer\s+[A-Za-z0-9._\-]{8,}/gi,
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}(?:\.[A-Za-z0-9_-]{8,})?/g,
  /[\w.+-]+@[\w-]+\.[\w.-]+/g,
  /(?<!\d)(?:\+?234|0)[789][01]\d{8}(?!\d)/g,
  /(?<!\d)\d{10}(?!\d)/g,
  /\b(?:otp|code|pin)\b\s*[=:]?\s*\d{4,8}\b/gi,
  /\b(?:pass(?:word|wd)?|pwd|secret|token)\b\s*[=:]\s*\S+/gi,
  /\b[A-Fa-f0-9]{32,}\b/g,
]

/** Replace anything that looks like a person or a credential. */
export function scrub(text: string): string {
  let out = text
  for (const pattern of PATTERNS) out = out.replace(pattern, REDACTED)
  return out
}

/** `scrub` applied to every string inside a value, however nested. Cycles are cut. */
export function scrubDeep<T>(value: T, seen = new WeakSet<object>()): T {
  if (typeof value === 'string') return scrub(value) as unknown as T
  if (value instanceof Error) {
    const copy = new Error(scrub(value.message))
    copy.name = value.name
    copy.stack = value.stack ? scrub(value.stack) : undefined
    return copy as unknown as T
  }
  if (Array.isArray(value)) return value.map((item) => scrubDeep(item, seen)) as unknown as T
  if (value && typeof value === 'object') {
    if (seen.has(value)) return value
    seen.add(value)
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      // Field names that are PII by definition are dropped outright.
      if (
        /^(email|phone|mobile_phone|ip_address|password|token|authorization|cookie)$/i.test(key)
      ) {
        out[key] = REDACTED
        continue
      }
      out[key] = scrubDeep(item, seen)
    }
    return out as unknown as T
  }
  return value
}

class ConsoleTracker implements ErrorTracker {
  capture(error: unknown, context?: Record<string, unknown>) {
    if (import.meta.env.MODE === 'test') return
    console.error('[apmp] unhandled error', scrubDeep(error), scrubDeep(context ?? {}))
  }

  captureMessage(message: string, context?: Record<string, unknown>) {
    if (import.meta.env.MODE === 'test') return
    console.warn('[apmp]', scrub(message), scrubDeep(context ?? {}))
  }
}

let active: ErrorTracker = new ConsoleTracker()

/** The current reporter. Components and boundaries call this; they never import Sentry. */
export function tracker(): ErrorTracker {
  return active
}

/** Test seam. */
export function setTracker(next: ErrorTracker | null): void {
  active = next ?? new ConsoleTracker()
}

export type TrackingMode = 'console' | 'sentry'

/**
 * Wire the reporter. Resolves to which one is active. Never throws: a broken DSN or a blocked
 * CDN must not stop the app from mounting.
 */
export async function initErrorTracking(app: App, router: Router): Promise<TrackingMode> {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return 'console'
  try {
    const Sentry = await import('@sentry/vue')
    Sentry.init({
      app,
      dsn,
      release: import.meta.env.VITE_APP_RELEASE || undefined,
      environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
      sendDefaultPii: false,
      integrations: [Sentry.browserTracingIntegration({ router })],
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
      // Vue's own error handler still runs (the boundary and app.config.errorHandler).
      attachProps: false,
      beforeSend(event) {
        const clean = scrubDeep(event)
        if (clean.user) clean.user = { id: clean.user.id }
        if (clean.request) {
          clean.request = { url: clean.request.url ? scrub(String(clean.request.url)) : undefined }
        }
        return clean
      },
      beforeBreadcrumb(breadcrumb) {
        return scrubDeep(breadcrumb)
      },
    })
    active = {
      capture: (error, context) =>
        Sentry.captureException(scrubDeep(error), { extra: scrubDeep(context ?? {}) }),
      captureMessage: (message, context) =>
        Sentry.captureMessage(scrub(message), { extra: scrubDeep(context ?? {}) }),
    }
    return 'sentry'
  } catch (cause) {
    new ConsoleTracker().captureMessage('error tracking failed to initialise', {
      cause: String(cause),
    })
    return 'console'
  }
}
